import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, ArrowsClockwise, Wrench, CaretDown, CaretRight } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import AttachmentsPanel from "../components/AttachmentsPanel";
import { StandardReports } from "../components/StandardReports";

const TABS = ["Assets", "Work Orders", "Reports"];
const fmt$ = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export default function EAMPage() {
  const [tab, setTab] = useState(new URLSearchParams(window.location.search).get("tab") || "Assets");
  const [summary, setSummary] = useState(null);
  const [assets, setAssets] = useState([]);
  const [wos, setWos] = useState([]);

  const refresh = async () => {
    try {
      const [s, a, w] = await Promise.all([
        api.get("/eam/summary").then((r) => r.data),
        api.get("/eam/assets").then((r) => r.data),
        api.get("/eam/work-orders").then((r) => r.data),
      ]);
      setSummary(s); setAssets(a); setWos(w);
    } catch (e) {
      if (e.response?.status === 402) toast.error("EAM module requires Enterprise plan or higher.");
    }
  };

  useEffect(() => { refresh(); }, []);

  return (
    <div className="p-6 sm:p-8" data-testid="eam-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400">MODULE / 07 · ENTERPRISE ASSETS</div>
          <h1 className="font-display text-4xl font-black tracking-tighter mt-1">Assets.</h1>
        </div>
        <button onClick={refresh} className="text-xs font-mono text-zinc-500 hover:text-zinc-950 flex items-center gap-1">
          <ArrowsClockwise size={12} /> REFRESH
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 border border-zinc-200 mb-6">
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">Total Assets</div>
            <div className="font-display font-black text-2xl tnum mt-1">{summary.total_assets}</div>
          </div>
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">Operational</div>
            <div className="font-display font-black text-2xl tnum mt-1 text-emerald-700">{summary.operational}</div>
          </div>
          <div className="border-r border-zinc-200 px-6 py-4">
            <div className="label-sm text-zinc-500">In Maintenance</div>
            <div className="font-display font-black text-2xl tnum mt-1 text-amber-700">{summary.in_maintenance}</div>
          </div>
          <div className="px-6 py-4">
            <div className="label-sm text-zinc-500">Asset Value</div>
            <div className="font-display font-black text-2xl tnum mt-1">{fmt$(summary.total_asset_value)}</div>
          </div>
        </div>
      )}

      <div className="flex border-b border-zinc-950">
        {TABS.map((t) => (
          <button
            key={t}
            data-testid={`eam-tab-${t.toLowerCase().replace(/\s/g, "-")}`}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-xs font-semibold tracking-[0.1em] uppercase border-r border-zinc-950 transition-colors ${
              tab === t ? "bg-zinc-950 text-white" : "bg-white text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="border-x border-b border-zinc-950">
        {tab === "Assets" && <Assets assets={assets} onChange={refresh} />}
        {tab === "Work Orders" && <WorkOrders wos={wos} assets={assets} onChange={refresh} />}
        {tab === "Reports" && <StandardReports module="eam" />}
      </div>
    </div>
  );
}

function Assets({ assets, onChange }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ asset_tag: "", name: "", category: "Production", location: "", value: "" });
  const [expanded, setExpanded] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/eam/assets", { ...form, value: Number(form.value) });
      toast.success("Asset added");
      setForm({ asset_tag: "", name: "", category: "Production", location: "", value: "" });
      onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (id, s) => {
    await api.patch(`/eam/assets/${id}?status=${s}`);
    onChange();
  };

  return (
    <div>
      <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-[120px_1fr_140px_1fr_140px_auto] gap-px bg-zinc-200 border-b border-zinc-950" data-testid="asset-form">
        <input value={form.asset_tag} onChange={(e) => set("asset_tag", e.target.value)} required placeholder="Tag (AST-...)" data-testid="asset-tag-input" className="bg-white px-3 py-3 text-sm outline-none tnum" />
        <input value={form.name} onChange={(e) => set("name", e.target.value)} required placeholder="Name" data-testid="asset-name-input" className="bg-white px-3 py-3 text-sm outline-none" />
        <select value={form.category} onChange={(e) => set("category", e.target.value)} className="bg-white px-3 py-3 text-sm outline-none">
          {["Production", "Logistics", "IT", "Facilities", "Vehicle"].map((c) => <option key={c}>{c}</option>)}
        </select>
        <input value={form.location} onChange={(e) => set("location", e.target.value)} required placeholder="Location" className="bg-white px-3 py-3 text-sm outline-none" />
        <input type="number" step="0.01" value={form.value} onChange={(e) => set("value", e.target.value)} placeholder="Value" className="bg-white px-3 py-3 text-sm outline-none tnum" />
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] flex items-center justify-center gap-1" data-testid="asset-create-btn">
          <Plus size={14} /> ADD
        </button>
      </form>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">Tag</th>
            <th className="text-left px-4 py-2.5">Asset</th>
            <th className="text-left px-4 py-2.5">Category</th>
            <th className="text-left px-4 py-2.5">Location</th>
            <th className="text-right px-4 py-2.5">Value</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="w-44" />
          </tr>
        </thead>
        <tbody data-testid="assets-tbody">
          {assets.map((a) => (
            <Fragment key={a.id}>
              <tr className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="px-4 py-2.5 tnum text-xs">
                  <button
                    onClick={() => setExpanded(expanded === a.id ? null : a.id)}
                    className="inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-950"
                    data-testid={`asset-expand-${a.id}`}
                    aria-label={expanded === a.id ? "Collapse" : "Expand"}
                  >
                    {expanded === a.id ? <CaretDown size={10} /> : <CaretRight size={10} />}
                    {a.asset_tag}
                  </button>
                </td>
                <td className="px-4 py-2.5 font-medium">{a.name}</td>
                <td className="px-4 py-2.5 text-zinc-500">{a.category}</td>
                <td className="px-4 py-2.5 text-zinc-500">{a.location}</td>
                <td className="px-4 py-2.5 text-right tnum">{fmt$(a.value)}</td>
                <td className="px-4 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 ${a.status === "operational" ? "bg-emerald-50 text-emerald-700" : a.status === "maintenance" ? "bg-amber-50 text-amber-700" : "bg-zinc-100"}`}>{a.status?.toUpperCase()}</span></td>
                <td className="px-4 py-2.5">
                  {a.status !== "maintenance" && <button onClick={() => setStatus(a.id, "maintenance")} className="text-[10px] font-mono text-amber-700 hover:underline mr-2">MAINT</button>}
                  {a.status !== "operational" && <button onClick={() => setStatus(a.id, "operational")} className="text-[10px] font-mono text-emerald-700 hover:underline">OK</button>}
                </td>
              </tr>
              {expanded === a.id && (
                <tr className="bg-zinc-50 border-b border-zinc-200">
                  <td colSpan={7} className="px-4 py-4">
                    <AttachmentsPanel
                      parentType="asset"
                      parentId={a.id}
                      canAttach={true}
                      currentUser={user}
                    />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WorkOrders({ wos, assets, onChange }) {
  const [form, setForm] = useState({ asset_id: "", type: "preventive", description: "", scheduled_for: "", priority: "medium" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/eam/work-orders", form);
      toast.success("Work order created");
      setForm({ asset_id: "", type: "preventive", description: "", scheduled_for: "", priority: "medium" });
      onChange();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const setStatus = async (id, s) => {
    await api.patch(`/eam/work-orders/${id}?status=${s}`);
    toast.success(`WO ${s}`);
    onChange();
  };

  return (
    <div>
      <form onSubmit={create} className="grid grid-cols-2 md:grid-cols-[1fr_140px_2fr_140px_120px_auto] gap-px bg-zinc-200 border-b border-zinc-950" data-testid="wo-form">
        <select value={form.asset_id} onChange={(e) => set("asset_id", e.target.value)} required data-testid="wo-asset-select" className="bg-white px-3 py-3 text-sm outline-none">
          <option value="">— asset —</option>
          {assets.map((a) => <option key={a.id} value={a.id}>{a.asset_tag} · {a.name}</option>)}
        </select>
        <select value={form.type} onChange={(e) => set("type", e.target.value)} className="bg-white px-3 py-3 text-sm outline-none">
          {["preventive", "corrective", "predictive", "inspection"].map((t) => <option key={t}>{t}</option>)}
        </select>
        <input value={form.description} onChange={(e) => set("description", e.target.value)} required placeholder="Description" data-testid="wo-desc-input" className="bg-white px-3 py-3 text-sm outline-none" />
        <input type="date" value={form.scheduled_for} onChange={(e) => set("scheduled_for", e.target.value)} className="bg-white px-3 py-3 text-sm outline-none tnum" />
        <select value={form.priority} onChange={(e) => set("priority", e.target.value)} className="bg-white px-3 py-3 text-sm outline-none">
          {["low", "medium", "high", "urgent"].map((p) => <option key={p}>{p}</option>)}
        </select>
        <button type="submit" className="bg-zinc-950 text-white text-xs font-semibold px-5 hover:bg-[#FF3E00] flex items-center justify-center gap-1" data-testid="wo-create-btn">
          <Wrench size={14} /> CREATE
        </button>
      </form>
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 border-b border-zinc-950">
          <tr className="text-xs tracking-wider uppercase">
            <th className="text-left px-4 py-2.5">WO #</th>
            <th className="text-left px-4 py-2.5">Asset</th>
            <th className="text-left px-4 py-2.5">Type</th>
            <th className="text-left px-4 py-2.5">Description</th>
            <th className="text-left px-4 py-2.5">Scheduled</th>
            <th className="text-left px-4 py-2.5">Priority</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="w-32" />
          </tr>
        </thead>
        <tbody data-testid="wos-tbody">
          {wos.map((w) => {
            const asset = assets.find((a) => a.id === w.asset_id);
            return (
              <tr key={w.id} className="border-b border-zinc-100">
                <td className="px-4 py-2.5 tnum text-xs">{w.wo_no}</td>
                <td className="px-4 py-2.5 text-xs">{asset ? asset.asset_tag : "—"}</td>
                <td className="px-4 py-2.5 capitalize text-zinc-500">{w.type}</td>
                <td className="px-4 py-2.5">{w.description}</td>
                <td className="px-4 py-2.5 tnum text-xs text-zinc-500">{w.scheduled_for || "—"}</td>
                <td className="px-4 py-2.5"><span className="text-[10px] px-1.5 py-0.5 bg-zinc-100">{w.priority?.toUpperCase()}</span></td>
                <td className="px-4 py-2.5"><span className={`text-[10px] px-1.5 py-0.5 ${w.status === "completed" ? "bg-emerald-50 text-emerald-700" : w.status === "in_progress" ? "bg-amber-50 text-amber-700" : "bg-zinc-100"}`}>{w.status?.toUpperCase()}</span></td>
                <td className="px-4 py-2.5">
                  {w.status === "open" && <button onClick={() => setStatus(w.id, "in_progress")} className="text-[10px] font-mono text-amber-700 hover:underline mr-2">START</button>}
                  {w.status !== "completed" && <button onClick={() => setStatus(w.id, "completed")} className="text-[10px] font-mono text-emerald-700 hover:underline">DONE</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
